import clsx from 'clsx';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import promptImage from 'resources/img/hs-prompt-image-2.jpg';
import { openAIAssistantWithExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import { useAiAssistantDisplayed } from '@/aiAssistant/aiAssistant.store';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { sqHomeScreenStore, sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TRAINING_PROMPT_ID, TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { genAIEnabled, isLearnModeEnabled, isNewHomeScreen2025Enabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { GET_STARTED_WITH_SEEQ_LINK, TRAINING_TELEMETRY_HOST } from '@/utilities/externalLinks';
import { infoToast, warnToast } from '@/utilities/toast.utilities';
import { Button, ExternalLink, Icon } from '@seeqdev/qomponents';

/**
 * This component renders content of the Training "Get Started" prompt for the Home Screen Page.
 * It DOES NOT include the outer box with the dismiss button.
 */
export const TrainingPromptContent = ({ isModal, onClose }: { isModal?: boolean; onClose?: () => void }) => {
  const { t } = useTranslation();
  const isTrusted = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.isTrusted);
  const userFirstName = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser.firstName);
  const isAiAssistantDisplayed = useAiAssistantDisplayed();
  const branchCode = useFluxPath(sqHomeScreenStore, () => sqHomeScreenStore.branchCode);
  const hasBranchCode = Boolean(branchCode?.trim());
  const firstLegacyStep = hasBranchCode
    ? t('HOME_SCREEN.TRAINING.STEP_1', { branchCode: `<b>${branchCode}</b>` })
    : t('HOME_SCREEN.TRAINING.STEP_1_NO_CODE', {
        email: '<a href="mailto:training@seeq.com">training@seeq.com</a>',
      });
  const steps = [
    firstLegacyStep,
    t('HOME_SCREEN.TRAINING.STEP_2', {
      website: `<a href="${TRAINING_TELEMETRY_HOST.href}">${TRAINING_TELEMETRY_HOST.label}</a>`,
    }),
    t('HOME_SCREEN.TRAINING.STEP_3'),
  ];
  const [videoClickCount, setVideoClickCount] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(moment.now());

  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();

  useEffect(() => {
    doTrack('Training', 'home screen intro video shown');
  }, []);

  const renderPromptHeader = (header: string, subheader: string) => (
    <div className="flexRowContainer flexSelfStart mb15">
      <h4
        className={clsx('mt0 text-bolder', {
          'tw:text-sq-homescreen-section-title': isNewHomeScreenEnabled,
          'homescreen-title-text tw:dark:text-white': !isNewHomeScreenEnabled,
        })}>
        {header}
      </h4>
      <span>{subheader}</span>
    </div>
  );

  const renderPromptFooter = (header: React.ReactNode, subheader?: string) => (
    <div className="flexRowContainer">
      <span>{header}</span>
      {subheader && <span>{subheader}</span>}
    </div>
  );

  const renderNewPromptContent = () => (
    <div className={clsx('flexRowContainer width-100percent')} data-testid="trainingPromptContentNew">
      <div
        className={clsx('width-100percent gap30', {
          [isModal ? 'flexRowContainer' : 'flexColumnContainer']: true,
        })}>
        <div className="flexRowContainer width-50-percent homeScreenSectionContainer tw:!bg-sq-homescreen-gray-bg tw:dark:!border-none tw:dark:!bg-transparent tw:dark:!p-0">
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.HANDS_ON_TRAINING'), t('HOME_SCREEN.TRAINING.REAL_USE_CASES'))}
          <ExternalLink
            href={TRAINING_TELEMETRY_HOST.href}
            extraClassNames="tw:h-[stretch] tw:max-h-[30vh] slightlyRoundedBorder tw:block tw:relative tw:overflow-hidden tw:no-underline tw:h-full tw:w-full">
            <img
              alt={t('HOME_SCREEN.TRAINING.PROMPT_IMAGE_ALT')}
              className="tw:absolute tw:inset-0 tw:bg-cover tw:bg-center"
              src={promptImage}
            />
          </ExternalLink>
          {renderPromptFooter(
            <>
              <ol className="noMarginBottom pl12">
                {!isLearnModeEnabled() &&
                  (hasBranchCode ? (
                    <li>
                      <CopyBranchCode text="HOME_SCREEN.TRAINING.COPY_BRANCH_CODE" branchCode={branchCode!} />
                    </li>
                  ) : (
                    <li>{t('HOME_SCREEN.TRAINING.NO_BRANCH_CODE')}</li>
                  ))}
                <li>
                  <ExternalLink
                    label={
                      isLearnModeEnabled() ? t('HOME_SCREEN.TRAINING.GO_TO') : t('HOME_SCREEN.TRAINING.REGISTER_ON')
                    }
                    href={TRAINING_TELEMETRY_HOST.href}>
                    {TRAINING_TELEMETRY_HOST.label}
                  </ExternalLink>
                </li>
              </ol>
            </>,
          )}
        </div>
        <div className="flexRowContainer homeScreenSectionContainer tw:w-1/2 tw:!bg-sq-homescreen-gray-bg tw:dark:!border-none tw:dark:!bg-transparent tw:dark:!p-0">
          {renderPromptHeader(t('HOME_SCREEN.TRAINING.WHAT_IS_SEEQ'), t('HOME_SCREEN.TRAINING.EXPLORE'))}
          <video
            className="height-maximum width-maximum slightlyRoundedBorder mb10"
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack('Training', 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack('Training', 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className="text-italic">
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href="https://www.seeq.com/product/our-offerings/" extraClassNames="ml5">
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
      </div>
    </div>
  );

  const renderLegacyPromptContent = () => (
    <div
      className={clsx('flexRowContainer width-100percent', {
        flexCenter: !isNewHomeScreenEnabled,
      })}
      data-testid="trainingPromptContent">
      <>
        <h1 className="text-bolder">{t('HOME_SCREEN.WELCOME')}</h1>
        <div className="mr20 ml20 text-center trainingPromptText">
          {t('HOME_SCREEN.NEW_USER_MESSAGE', { name: userFirstName })}
        </div>
      </>
      <div
        className={clsx('lightGreyBorderTop tw:mt-3 tw:w-full tw:justify-center tw:gap-1 tw:pt-3', {
          [isModal ? 'flexRowContainer' : 'flexColumnContainer']: true,
        })}>
        <div
          className={clsx('flexRowContainer tw:items-center tw:p-3', {
            homeScreenVideo: !isModal,
          })}>
          <video
            className={clsx('height-maximum width-maximum slightlyRoundedBorder mb10', {
              'tw:max-w-[35vh]': !isModal,
            })}
            controls={true}
            autoPlay={false}
            disablePictureInPicture={true}
            controlsList="nodownload"
            onClick={() => {
              if (videoClickCount % 2) {
                const endTime = moment.now();
                const duration = endTime - videoStartTime;
                doTrack('Training', 'Homescreen video played v2 for', moment(duration).format('mm:ss'));
              }

              if (!videoClickCount) {
                doTrack('Training', 'Homescreen video played v2');
              }
              setVideoClickCount(videoClickCount + 1);
              setVideoStartTime(moment.now());
            }}>
            <source src="https://telemetry.seeq.com/seeq-intro-video" />
          </video>
          {renderPromptFooter(
            <div className={clsx('text-italic mt5 xsmall')}>
              {t('VIDEO.TROUBLE_VIEWING')}
              <ExternalLink href={GET_STARTED_WITH_SEEQ_LINK} extraClassNames="ml5">
                {t('VIDEO.WATCH_HERE')}
              </ExternalLink>
            </div>,
          )}
        </div>
        <div className="flexRowContainer tw:justify-center">
          {genAIEnabled() && isTrusted && (
            <div className="flexRowContainer flexAlignCenter mb10">
              <Button
                label={t('AI_ASSISTANT.GET_STARTED')}
                icon="fa-sparkles"
                variant="theme"
                onClick={() => {
                  doTrack('AiAssistant', 'get started prompt clicked');
                  openAIAssistantWithExternalPrompt(
                    { prompt: t('AI_ASSISTANT.GET_STARTED_PROMPT'), agent: 'general' },
                    { wasDisplayed: isAiAssistantDisplayed },
                  );
                  isModal && onClose && onClose();
                }}
              />
            </div>
          )}
          <div className="trainingPrompt">
            {hasBranchCode ? t('HOME_SCREEN.TRAINING.NEXT_BRANCH_PROMPT') : t('HOME_SCREEN.TRAINING.NEXT_PROMPT')}
          </div>
          {hasBranchCode && <div className="trainingSubtitle">{t('HOME_SCREEN.TRAINING.NEXT_BRANCH_SUBTITLE')}</div>}
          <ol className="trainingPromptText">
            {steps.map((step: string, idx: number) => (
              <li key={idx}>
                <ContainerWithHTML content={step} />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );

  return isNewHomeScreenEnabled ? renderNewPromptContent() : renderLegacyPromptContent();
};

/**
 * This component renders the Training "Get Started" prompt for the Home Screen Page.
 * It includes the outer box with the dismiss button.
 */
export const TrainingPrompt = () => {
  const isNewHomeScreenEnabled = isNewHomeScreen2025Enabled();
  const { t } = useTranslation();

  const renderLegacyPanel = () => (
    <div className="flexRowContainer flexAlignCenter positionRelative roundedBorder lightGreyBorder ml20 mr20 mb20">
      <div className="positionAbsolute welcomeActions flexColumnContainer">
        <span className="mr5">{t('HOME_SCREEN.TRAINING.HIDE_THIS_PANEL')}</span>
        <Icon
          onClick={() => {
            setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
            doTrack('Training', 'get started prompt closed');
            warnToast({ messageKey: 'HOME_SCREEN.RESUME_TRAINING' });
          }}
          icon="fa-close"
          extraClassNames="fa-xlg cursorPointer hoverGray"
          testId="trainingDismissIcon"
          type={isNewHomeScreenEnabled ? undefined : 'gray'}
        />
      </div>
      <TrainingPromptContent />
    </div>
  );

  const dismissTraining = () => {
    setTrainingPromptDisplay(TrainingPromptDisplay.NONE);
    doTrack('Training', 'get started prompt closed');
    infoToast({ messageKey: 'HOME_SCREEN.TRAINING.DISMISS_TRAINING_TEXT' });
  };

  const renderNewPanel = () => (
    <div id={TRAINING_PROMPT_ID} className="flexRowContainer homeScreenSectionContainer tw:dark:!border-none">
      <div className="flexColumnContainer flexSpaceBetween mb10 flexAlignEnd negativeTopMargin10">
        <h3 className="m0">{t('HOME_SCREEN.TRAINING.TRAINING_TITLE')}</h3>
        <div className="flexColumnContainer flexAlignCenter">
          <span className="mr5 fs15 homescreen-action cursorPointer" onClick={dismissTraining}>
            {t('HOME_SCREEN.TRAINING.HIDE_WINDOW')}
          </span>
          <Icon
            onClick={dismissTraining}
            icon="fa-close"
            extraClassNames="fa-xlg cursorPointer hoverGray homescreen-action"
            testId="trainingDismissIcon"
            type={isNewHomeScreenEnabled ? undefined : 'gray'}
          />
        </div>
      </div>
      <TrainingPromptContent />
    </div>
  );
  return isNewHomeScreenEnabled ? renderNewPanel() : renderLegacyPanel();
};
